import { Store, storeToRefs } from "pinia";
import { isNil } from "lodash";
import { computed, nextTick, ref } from "vue";
import { useRoute } from "vue-router/composables";
import { SavingType } from "@rmp/core/types/SavingType";
import { FormActions, FormGetters, FormState, FormStore } from "@rmp/core/stores/composables/form/useFormStore";
import { SnapshotActions, SnapshotGetters, SnapshotState } from "@rmp/core/stores/composables/snapshot";

export type FormSnapshotStore = Store<string, FormState & SnapshotState, FormGetters & SnapshotGetters, FormActions & SnapshotActions>;

export interface FormOptions {
	useStore: () => FormStore;
}

const isFieldEmpty = (value: any): boolean => isNil(value) || value === "";

export function useForm(options: FormOptions, isResetValidationDisabled: boolean = false) {
	const store = options.useStore() as FormSnapshotStore;
	
	const form = ref<any>(null);
	
	const {
		formStateLoading,
		formSaving,
		formDisabled,
		formValid,
		formReadonly,
		formRecordUnique,
		formRecordUniqueCheckInProgress,
		stateContainsChangesSinceLastSaved,
		changedSinceLastSavedFields
	} = storeToRefs(store);
	
	const route = useRoute();
	
	const createMode = computed(() => {
		return !route.params.id;
	});
	
	const validateForm = () => {
		return form.value.validate();
	};
	
	const validateField = (name: string | string[], options: { skipEmpty?: boolean } = {}) => {
		const names = Array.isArray(name) ? name : [name];
		
		names.forEach(n => {
			const input = form.value?.inputs?.find((i: any) => i.$attrs?.name === n || i.$attrs?.id === n);
			if(!input) return;
			if(options.skipEmpty && isFieldEmpty(input.value)) return;
			
			input.validate(true);
		});
	};
	
	const validateFilledFields = () => {
		form.value.inputs.forEach((input: any) => {
			if(!isFieldEmpty(input.value)) {
				input.validate(true);
			}
		});
	};
	
	const onFormCancel = async () => {
		await cancelFormChanges();
		await nextTick(() => {
			if(!isResetValidationDisabled)
				form.value.resetValidation();
		});
	};
	
	const cancelFormChanges = async () => {
		await store.cancelChanges();
	};
	
	const submit = async () => {
		if(validateForm()) {
			createMode ?
				await store.save({ type: SavingType.CREATE }) :
				await store.save({ type: SavingType.UPDATE, id: route.params.id });
		}
	};
	
	const setStateSnapshot = store.setStateSnapshot;
	
	return {
		form,
		formStateLoading,
		formSaving,
		formDisabled,
		formValid,
		formReadonly,
		formRecordUnique,
		formRecordUniqueCheckInProgress,
		stateContainsChangesSinceLastSaved,
		changedSinceLastSavedFields,
		createMode,
		validateForm,
		validateField,
		validateFilledFields,
		onFormCancel,
		cancelFormChanges,
		submit,
		setStateSnapshot
	};
}
